
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { SocialIcons } from '../ui/SocialIcons';
import { ArrowUpRight } from 'lucide-react';
import { GhostBrandIcon } from '../ui/PartnerIcons';

interface FooterProps {
    onLogin: () => void;
    onNavigateToDocs: (section: string) => void;
    onNavigate?: (page: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ onLogin, onNavigateToDocs, onNavigate }) => {
  const handleNav = (page: string) => {
      if (onNavigate) {
          onNavigate(page);
          window.scrollTo(0, 0);
      }
  };

  return (
    <footer className="bg-stone-50 dark:bg-[#050505] text-stone-600 dark:text-stone-400 py-24 border-t border-stone-200 dark:border-[#1a1a1a] transition-colors duration-300">
      <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-16 md:gap-12 mb-20 text-center md:text-left">
              
              {/* Branding Section */}
              <div className="space-y-8 flex flex-col items-center md:items-start">
                  <div 
                    className="flex flex-col md:flex-row items-center gap-4 text-[#0A0B0F] dark:text-white font-serif text-3xl justify-center md:justify-start group cursor-pointer" 
                    onClick={() => handleNav('landing')}
                  >
                      <div className="w-11 h-11 flex items-center justify-center transition-transform group-hover:scale-110">
                          <GhostBrandIcon className="w-full h-full" />
                      </div>
                      <span className="tracking-tight"><span className="font-light">Ghost</span><span className="font-bold">Pay</span></span>
                  </div>
                  <p className="text-sm text-stone-500 dark:text-stone-500 leading-relaxed max-w-sm">
                      High-performance operating system for the Ghost Network. Sovereign financial infrastructure.
                  </p>
                  <div className="pt-2">
                    <SocialIcons />
                  </div>
              </div>
              
              {/* Links Sections */}
              <div className="space-y-6">
                  <h4 className="text-[#0A0B0F] dark:text-white font-black uppercase tracking-[0.25em] text-[11px]">Ecosystem</h4>
                  <ul className="space-y-4 text-sm font-medium">
                      <li><button onClick={() => handleNav('about-landing')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">About Mission</button></li>
                      <li><button onClick={() => onNavigateToDocs('mobile-app')} className="hover:text-nobel-gold dark:hover:text-white transition-colors flex items-center gap-2 group justify-center md:justify-start mx-auto md:mx-0">GhostPay OS <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity"/></button></li>
                      <li><button onClick={() => onNavigateToDocs('dashboard')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">GhostPOS Dashboard</button></li>
                      <li><button onClick={() => onNavigateToDocs('core-features')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">Ghost Card (Titanium)</button></li>
                      <li><a href="https://medium.com/@ghostwallet" target="_blank" rel="noopener noreferrer" className="hover:text-nobel-gold dark:hover:text-white transition-colors flex items-center gap-2 group justify-center md:justify-start mx-auto md:mx-0">Official Blog <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity"/></a></li>
                  </ul>
              </div>

              <div className="space-y-6">
                  <h4 className="text-[#0A0B0F] dark:text-white font-black uppercase tracking-[0.25em] text-[11px]">Technical</h4>
                  <ul className="space-y-4 text-sm font-medium">
                      <li><button onClick={() => onNavigateToDocs('introduction')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">Documentation</button></li>
                      <li><button onClick={() => onNavigateToDocs('api-reference')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">API Reference</button></li>
                      <li><button onClick={() => onNavigateToDocs('brand-guide')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">Brand Guide</button></li>
                      <li><button onClick={() => onNavigateToDocs('contracts')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">Smart Contracts</button></li>
                  </ul>
              </div>

               <div className="space-y-6">
                  <h4 className="text-[#0A0B0F] dark:text-white font-black uppercase tracking-[0.25em] text-[11px]">Legals</h4>
                  <ul className="space-y-4 text-sm font-medium">
                      <li><button onClick={() => handleNav('legal')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">Legal & Privacy</button></li>
                      <li><button onClick={() => handleNav('legal')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">Cookie Protocol</button></li>
                      <li><button onClick={() => handleNav('legal')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">Risk Disclaimer</button></li>
                      <li><button onClick={() => onNavigateToDocs('architecture')} className="hover:text-nobel-gold dark:hover:text-white transition-colors">Security Audit</button></li>
                  </ul>
              </div>
          </div>
          
          <div className="pt-10 border-t border-stone-200 dark:border-white/5 flex flex-col md:flex-row justify-between items-center gap-8 text-center md:text-left">
              <div className="text-[10px] text-stone-400 dark:text-stone-600 font-mono uppercase tracking-[0.3em] font-bold">
                  &copy; 2026 GHOST NETWORK. ALL RIGHTS RESERVED.
              </div>
              <div className="flex flex-wrap justify-center gap-6 md:gap-8 text-[10px] font-black uppercase tracking-widest text-stone-500">
                  <button className="hover:text-nobel-gold transition-colors">System Status</button>
                  <button className="hover:text-nobel-gold transition-colors">Audit Ledger</button>
                  <button className="hover:text-nobel-gold transition-colors">Network Map</button>
              </div>
          </div>
      </div>
   </footer>
  );
};
